import { twMerge } from "tailwind-merge";
import { SupplierRelationshipEnumType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useUserInviteList } from "../../../hooks/api/v2/userInvite/useUserInviteList";
import { useBusinessUserList } from "../../../hooks/api/v2/businessUser/useBusinessUserList";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H3,
  H5,
  H6,
  Icon,
} from "@sellar-io/components";
import { ProfilePicture, SpinnerLegacy } from "../../../components";
import { CustomerContactCreateModal } from "../CustomerContactCreateModal";
import { CustomerContactDeleteModal } from "../CustomerContactDeleteModal";
import { AlertCompact } from "../../../components/alert/AlertCompact";

export const BusinessConnectionPeople = ({
  RetailerId,
  buyerName,
  connectionType,
}: {
  RetailerId: number;
  buyerName: string;
  connectionType: SupplierRelationshipEnumType;
}) => {
  const [customerContactCreateActive, setCustomerContactCreateActive] =
    useState<boolean>(false);

  const { data: userInviteListData, isLoading: isUserInviteListLoading } =
    useUserInviteList({
      query: [
        {
          value: RetailerId,
          type: "RetailerId",
          modifier: "EQUALS",
        },
      ],
    });

  const userInviteList = userInviteListData?.pages.flat() || [];

  const { data: businessUsersData, isLoading: isBusinessUsersLoading } =
    useBusinessUserList({
      query: [
        {
          value: RetailerId,
          type: "RetailerId",
          modifier: "EQUALS",
        },
      ],
    });

  const businessUsers = businessUsersData?.pages.flat() || [];

  const isLoading = isUserInviteListLoading || isBusinessUsersLoading;

  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge("bg-white", "flex items-center justify-between")}
        >
          <H3>People</H3>
        </CardHeader>
        {isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            {!!businessUsers.length && connectionType === "customer" && (
              <CardContent className={"pb-4"}>
                <div className={"flex flex-col gap-2"}>
                  <H5 className={"col-span-2 mb-2"}>
                    {businessUsers.length} active users at {buyerName}
                  </H5>
                  {businessUsers.map((businessUser) => (
                    <BusinessUserRow
                      key={businessUser.id}
                      businessUser={businessUser}
                    />
                  ))}
                </div>
              </CardContent>
            )}

            <CardContent>
              <div className={"flex flex-col gap-4"}>
                <div className="flex items-center justify-between gap-2">
                  <H5>My contacts</H5>
                  <DataButton
                    border={true}
                    onClick={() => setCustomerContactCreateActive(true)}
                  >
                    <Icon icon={"PLUS"} />
                    Add Contact
                  </DataButton>
                </div>
                <H6>
                  Contacts you add will receive order status email notifications
                  when you process their orders. Any contacts that are active
                  Sellar users will receive emails based on their notification
                  preferences.
                </H6>
                {!!userInviteList.length ? (
                  <div className="flex flex-col divide-y overflow-hidden rounded-lg border border-zinc-300 bg-white">
                    <div className="flex bg-zinc-50 font-semibold">
                      <span className="px-4 py-2">Email</span>
                    </div>
                    {userInviteList.map((userInvite) => (
                      <UserInviteRow
                        key={userInvite.id}
                        userInvite={userInvite}
                      />
                    ))}
                  </div>
                ) : (
                  <AlertCompact>Add a contact to see them here</AlertCompact>
                )}
              </div>
            </CardContent>
          </>
        )}
      </Card>
      <CustomerContactCreateModal
        active={customerContactCreateActive}
        onDismiss={() => setCustomerContactCreateActive(false)}
        RetailerId={RetailerId}
      />
    </>
  );
};

const BusinessUserRow = ({
  businessUser,
}: {
  businessUser: {
    id: number;
    User: {
      name: string | null;
    };
  };
}) => {
  return (
    <div
      key={businessUser.id}
      className="flex flex-row items-center justify-between gap-2"
    >
      <div className={"flex items-center gap-2"}>
        <ProfilePicture name={businessUser.User.name ?? ""} />
        <H5>{businessUser.User.name}</H5>
      </div>
    </div>
  );
};

const UserInviteRow = ({
  userInvite,
}: {
  userInvite: { id: number; email: string };
}) => {
  const [deleteModalActive, setDeleteModalActive] = useState(false);

  return (
    <>
      <div className="flex px-4 py-2">
        <span className={"flex items-center"}>{userInvite.email}</span>
        <div className={"ml-auto flex items-center"}>
          <Button color={"tertiary"} size="small">
            <Icon
              icon={"DELETE"}
              className={"cursor-pointer"}
              onClick={() => setDeleteModalActive(true)}
            />
          </Button>
        </div>
      </div>
      <CustomerContactDeleteModal
        active={deleteModalActive}
        userInviteId={userInvite.id}
        onDismiss={() => setDeleteModalActive(false)}
      />
    </>
  );
};
